import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { generateInvoiceHTML } from "../utils/invoicePrintTemplate";

/**
 * Custom hook for handling invoice printing functionality
 * Provides print and preview capabilities with loading states and error handling
 */
export const usePrintInvoice = () => {
  const { t } = useTranslation();
  const [isPrinting, setIsPrinting] = useState(false);
  const isPrintingRef = useRef(false);

  /**
   * Opens a new window and prints the invoice
   * @param {Object} invoiceData - All data needed to generate the invoice
   */
  const handlePrint = useCallback(async (invoiceData) => {
    if (!invoiceData || isPrintingRef.current) return;
    
    try {
      isPrintingRef.current = true;
      setIsPrinting(true);
      // A4-like aspect ratio (210×297) for print preview: ~595×842 px
      const printWindow = window.open('', '_blank', 'width=595,height=842,scrollbars=yes');
      
      if (printWindow) {
        const htmlContent = generateInvoiceHTML(invoiceData, t);
        printWindow.document.write(htmlContent);
        printWindow.document.close();
      } else {
        alert(t("Please allow popups for this website to print the invoice"));
      }
    } catch (error) {
      console.error('Print error:', error);
      alert(t("Failed to print invoice. Please try again."));
    } finally {
      isPrintingRef.current = false;
      setIsPrinting(false);
    }
  }, [t]);

  /**
   * Opens a new window with invoice preview (without auto-print)
   * @param {Object} invoiceData - All data needed to generate the invoice
   */
  const handlePreview = useCallback((invoiceData) => {
    if (!invoiceData) return;
    
    try {
      // A4 portrait preview: 210mm ≈ 794px, 297mm ≈ 1123px at 96dpi
      const previewWindow = window.open('', '_blank', 'width=794,height=1123,scrollbars=yes');
      
      if (previewWindow) {
        const htmlContent = generateInvoiceHTML(invoiceData, t, true); // true = preview mode
        previewWindow.document.write(htmlContent);
        previewWindow.document.close();
      } else {
        alert(t("Please allow popups for this website to preview the invoice"));
      }
    } catch (error) {
      console.error('Preview error:', error);
      alert(t("Failed to preview invoice. Please try again."));
    }
  }, [t]);

  return {
    isPrinting,
    handlePrint,
    handlePreview
  };
};

